import {
  Module,
  VuexModule,
  Mutation,
  getModule,
  Action,
} from "vuex-module-decorators";
import { RecordInfo } from "../../data/models/RecordInfo";
import User, {
  MASTER_ID,
  doesUserBelongToBranch,
  pickUserPreferringActive,
} from "../../data/models/User";
import { getUserDisplayName } from "../../utils/UserUtils";

import store from "@/store";
import {
  hasUserDryingProjectLicence,
  hasUserLeakDetectionProjectLicence,
  getLicencesOfUser,
} from "@/data/repository/UserRepository";
import { isSimulatingErp } from "@/data/repository/ClientRepository";
import Vue from "vue";

const name = "users";
export const DEFAULT_ADMIN_ID = "DEFAULT_ADMIN_ID";
export const IRES_ADMIN_ACCOUNT_ID = "IRES_ADMIN_ACCOUNT_ID";

if (store.state[name]) {
  store.unregisterModule(name);
}

@Module({ dynamic: true, namespaced: true, store, name: name })
export class UserVuexModule extends VuexModule {
  users: Array<User> = [];
  activeUserId: string | null = null;
  sharedUserPassword: string | null = null;

  @Mutation
  setUsers(users: Array<User>) {
    this.users = users;
  }

  @Mutation
  clear() {
    this.users = [];
  }

  @Mutation
  setActiveUserId(userId: string) {
    this.activeUserId = userId;
  }

  @Mutation
  setSharedUserPassword(password: string) {
    this.sharedUserPassword = password;
  }

  get hasActiveUserDryingProjectLicence() {
    return hasUserDryingProjectLicence(this.activeUser);
  }

  get hasActiveUserLeakDetectionProjectLicence() {
    return hasUserLeakDetectionProjectLicence(this.activeUser);
  }

  get licences() {
    return getLicencesOfUser(this.activeUser);
  }

  get activeUserIdentifier() {
    const activeUser = this.activeUser;

    const nickname = activeUser?.nickname && activeUser.nickname !== "" ? activeUser.nickname : null;
    const staffNumber = activeUser?.staffNumber && activeUser.staffNumber !== "" ? activeUser.staffNumber : null;

    const activeUserIdentifier = staffNumber ?? nickname ?? activeUser?.id ?? MASTER_ID;

    return isSimulatingErp() ? "ERP/"+activeUserIdentifier : activeUserIdentifier;
  }

  get activeUsersOfClient() {
    return this.users
      .filter(
        (user: User) => !user.deleted && user.id !== IRES_ADMIN_ACCOUNT_ID
      )
      .sort((u1, u2) =>
        getUserDisplayName(u1).localeCompare(getUserDisplayName(u2))
      );
  }

  get activeUsersOfClientIncludingIresAdmin() {
    return this.users
      .filter(
        (user: User) => !user.deleted
      )
      .sort((u1, u2) =>
        getUserDisplayName(u1).localeCompare(getUserDisplayName(u2))
      );
  }

  get usersForSelection(): Array<User> {
    const branchesOfActiveUser = this.activeUser?.branches ?? [];

    const usersForSelection = this.activeUsersOfClient
      .filter(
        (user: User) =>
          branchesOfActiveUser.length === 0 ||
          branchesOfActiveUser.some((branchId) =>
            doesUserBelongToBranch(user, branchId)
          )
      )
      .sort((u1, u2) =>
        getUserDisplayName(u1).localeCompare(getUserDisplayName(u2))
      );

    if (usersForSelection.length > 0) {
      return usersForSelection;
    } else {
      return [this.master];
    }
  }

  get master(): User {
    return {
      id: MASTER_ID,
      nickname: "Master",
    };
  }

  get activeUser() {
    const activeUserId = this.activeUserId;

    if (activeUserId) {
      return this.user(activeUserId);
    } else {
      return this.master;
    }
  }

  get user(): (userId: string) => User | null {
    return (userId: string) => {
      if (userId === MASTER_ID) {
        return this.master;
      }

      const users = this.users;

      // Each stage prefers an active user: one identifier (a shared staffNumber above all) can match
      // several users, and a deleted account must not win over an active one.
      return (
        pickUserPreferringActive(users, (user) => user.id === userId) ??
        pickUserPreferringActive(users, (user) => user.staffNumber === userId) ??
        pickUserPreferringActive(users, (user) => user.nickname === userId) ??
        null
      );
    };
  }

  get userNicknameByStaffNumber(): (staffNumber: string) => string | null {
    return (staffNumber: string) => {
      if (staffNumber === "") {
        return null;
      }

      const users = this.users;
      const user = pickUserPreferringActive(
        users,
        (user) => user.staffNumber === staffNumber
      );

      if (!user) {
        return staffNumber;
      }

      return (
        user.nickname ?? user.id
      );
    };
  }

  get userIdByStaffNumber(): (staffNumber: string) => string {
    return (staffNumber: string) => {
      if (!staffNumber) {
        return staffNumber;
      }

      const users = this.users;
      const user = pickUserPreferringActive(
        users,
        (user) => user.staffNumber === staffNumber
      );

      if (!user) {
        return staffNumber;
      }

      return (
        user.id
      );
    };
  }

  get userNicknameFromRecordInfo() {
    return (recordInfo: RecordInfo | null | undefined) =>
      this.userNickname(recordInfo?.recordedByUserId);
  }

  get userNickname() {
    return (userId: string | null | undefined) => {
      if (!userId) {
        return "-";
      }

      return this.user(isSimulatingErp() && userId.includes("-") ? userId.split("-")[1] : userId)?.nickname ?? userId;
    };
  }

  get userNicknameOrLastNameOrStaffNumber() {
    return (vue: Vue, userId: string | null | undefined) => {
      if (!userId) {
        return "-";
      }

      const user = this.user(isSimulatingErp() && userId.includes("-") ? userId.split("-")[1] : userId);

      if (!user) {
        return "-"
      }

      const nickname = user.nickname && user.nickname !== "" ? user.nickname : null;
      const lastName = user.lastName && user.lastName !== "" ? user.lastName : null;
      const staffNumber = user.staffNumber && user.staffNumber !== "" ? user.staffNumber : null;

      return nickname ?? 
        lastName ?? 
        staffNumber ??
        vue.$t("common.unknown").toString();
    }; 
  }

  @Action
  getUser(userId: string | null | undefined): User | null {
    if (userId && userId !== DEFAULT_ADMIN_ID) {
      return this.user(userId);
    } else {
      return null;
    }
  }
}

export const UserModule = getModule(UserVuexModule, store);
